import { Table, Td, Text, Th } from '@ui'
import { UNITS, formatDuration, formatPace } from '../format.js'

// Time at distance d, interpolated between the two points either side of it.
function timeAt(points, d) {
  for (let i = 1; i < points.length; i++) {
    if (points[i].d >= d) {
      const a = points[i - 1]
      const b = points[i]
      const f = b.d > a.d ? (d - a.d) / (b.d - a.d) : 0
      return a.t + f * (b.t - a.t)
    }
  }
  return points[points.length - 1].t
}

function eleAt(points, d) {
  let last = null
  for (const p of points) {
    if (p.ele != null) last = p.ele
    if (p.d >= d) break
  }
  return last
}

// One row per whole mile/km plus the part split at the end. Times are elapsed along the
// route, so a pause lands in the split it happened in (the app doesn't store pauses).
// briskAt: where along the route each brisk minute was (the track's steps.brisk_at), counted
// whole into the split holding its middle, so the column adds up to the walk's brisk minutes;
// without it (no step readings) there's no Brisk column.
export default function Splits({ points, unit, briskAt }) {
  if (points.length < 2) return null
  const unitMeters = UNITS[unit].meters
  const total = points[points.length - 1].d
  const rows = []
  for (let start = 0; start < total - 1; start += unitMeters) {
    const end = Math.min(start + unitMeters, total)
    const seconds = timeAt(points, end) - timeAt(points, start)
    const eleStart = eleAt(points, start)
    const eleEnd = eleAt(points, end)
    rows.push({
      index: rows.length + 1,
      meters: end - start,
      ms: seconds * 1000,
      cumulativeMs: timeAt(points, end) * 1000,
      ele: eleStart != null && eleEnd != null ? eleEnd - eleStart : null,
    })
  }
  // A last part split under 50 m is just the end of the walk, not worth a row of its own.
  if (rows.length > 1 && rows[rows.length - 1].meters < 50) rows.pop()
  if (briskAt) {
    for (const r of rows) r.brisk = 0
    for (const d of briskAt) {
      // Past the last row's start (a dropped short part split, or the very end) counts in the last row.
      const i = Math.min(rows.length - 1, Math.max(0, Math.floor(d / unitMeters)))
      rows[i].brisk += 1
    }
  }

  return (
    // On a phone the Distance column goes (it's a whole mile/km on every row but the last, whose
    // part distance then shows by its number), so the table fits; if it still doesn't, it scrolls
    // sideways in its own box rather than pushing the page wider.
    <Table autoWidth responsive>
      <thead>
        <tr>
          <th>{unit === 'mi' ? 'Mile' : 'Km'}</th>
          <Th numeric hideBelow="sm">Distance</Th>
          <Th numeric>Time</Th>
          <Th numeric>Pace</Th>
          <Th numeric>Elapsed</Th>
          <Th numeric>Elevation</Th>
          {briskAt && <Th numeric><abbr title="Brisk minutes in the split: clock minutes with 100 steps or more">Brisk min</abbr></Th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.index}>
            <td>
              {r.index}
              {Math.abs(r.meters - unitMeters) > 0.5 && <Text hideAbove="sm" muted> ({(r.meters / unitMeters).toFixed(2)})</Text>}
            </td>
            <Td numeric hideBelow="sm">{(r.meters / unitMeters).toFixed(2)}</Td>
            <Td numeric>{formatDuration(r.ms)}</Td>
            <Td numeric>{formatPace(r.ms, r.meters, unit)}</Td>
            <Td numeric>{formatDuration(r.cumulativeMs)}</Td>
            <Td numeric>{r.ele == null ? '–' : `${r.ele >= 0 ? '+' : '−'}${Math.abs(Math.round(r.ele))} m`}</Td>
            {briskAt && <Td numeric>{r.brisk}</Td>}
          </tr>
        ))}
      </tbody>
    </Table>
  )
}
